import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Scale, Calendar, Clock, CheckCircle2, XCircle, Info, AlertTriangle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
}
function fmtHour(iso) {
  return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}
function fmtFull(iso) {
  return new Date(iso).toLocaleString("it-IT", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const STATO_LABELS = {
  richiesto: { label: "In attesa di conferma", color: "amber" },
  confermato: { label: "Confermato", color: "emerald" },
  riprogrammato: { label: "Riprogrammato", color: "blue" },
  annullato: { label: "Annullato", color: "slate" },
  completato: { label: "Completato", color: "purple" },
};

export default function AppuntamentoAvvocatoSection() {
  const [slots, setSlots] = useState([]);
  const [hasActive, setHasActive] = useState(false);
  const [mieList, setMieList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [argomento, setArgomento] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [telefono, setTelefono] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/appuntamento-avvocato/slots`, { withCredentials: true }),
      axios.get(`${API}/api/appuntamento-avvocato/mie`, { withCredentials: true }),
    ])
      .then(([sR, mR]) => {
        setSlots(sR.data.slots || []);
        setHasActive(!!sR.data.mia_richiesta_attiva);
        setMieList(mR.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Raggruppa slot per giorno
  const slotsByDay = slots.reduce((acc, s) => {
    const key = new Date(s.slot).toISOString().slice(0, 10);
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || !argomento) {
      toast({ description: "Seleziona uno slot e indica l'argomento.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/appuntamento-avvocato/prenota`,
        { slot: selected, argomento, descrizione, telefono },
        { withCredentials: true }
      );
      toast({ description: "Richiesta inviata! La segreteria ti confermerà via email." });
      setSelected(null); setArgomento(""); setDescrizione(""); setTelefono("");
      load();
    } catch (err) {
      toast({ description: "Errore: " + (err.response?.data?.detail || err.message), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Vuoi annullare questa prenotazione?")) return;
    try {
      await axios.post(`${API}/api/appuntamento-avvocato/${id}/annulla`, {}, { withCredentials: true });
      toast({ description: "Prenotazione annullata." });
      load();
    } catch (err) {
      toast({ description: "Errore: " + (err.response?.data?.detail || err.message), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="appuntamento-avvocato-section">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-8 md:p-10 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center flex-shrink-0">
            <Scale className="h-6 w-6" />
          </div>
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#fbbf24]">Consulenza legale inclusa</div>
            <h1 className="mt-1 text-2xl md:text-3xl font-extrabold leading-tight">Prenota il tuo appuntamento con l&apos;avvocato</h1>
            <p className="mt-2 text-[14.5px] text-slate-200 max-w-2xl leading-relaxed">
              Come socio A.C.D.T. hai diritto a un colloquio gratuito con un avvocato dell&apos;Associazione
              su tutela digitale, privacy, contratti online e diritti del consumatore.
            </p>
          </div>
        </div>
      </div>

      {/* Le mie prenotazioni */}
      {mieList.length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6">
          <h2 className="text-lg font-extrabold text-[#0a1f55] mb-4">Le mie prenotazioni</h2>
          <div className="space-y-3">
            {mieList.map((m) => {
              const s = STATO_LABELS[m.stato] || { label: m.stato, color: "slate" };
              return (
                <div key={m.id} className={`rounded-xl border p-4 flex flex-wrap items-center gap-4 border-${s.color}-200 bg-${s.color}-50`}>
                  <Calendar className={`h-5 w-5 text-${s.color}-600 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0a1f55] text-[15px]">{fmtFull(m.slot)}</div>
                    <div className="text-[12.5px] text-slate-600 mt-0.5">
                      <strong>Argomento:</strong> {m.argomento}
                    </div>
                    {m.note_admin && (
                      <div className="text-[12px] text-slate-700 mt-1 italic">
                        <strong>Nota segreteria:</strong> {m.note_admin}
                      </div>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10.5px] font-extrabold uppercase tracking-widest bg-${s.color}-500/15 text-${s.color}-700`}>
                    {s.label}
                  </span>
                  {(m.stato === "richiesto" || m.stato === "confermato") && (
                    <button
                      onClick={() => cancel(m.id)}
                      data-testid={`annulla-${m.id}`}
                      className="text-xs font-bold text-red-600 hover:text-red-800 underline"
                    >
                      Annulla
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasActive ? (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-[13.5px] text-amber-900">
            Hai già una richiesta di appuntamento attiva. Attendi la conferma della segreteria o
            annulla la richiesta corrente prima di prenotarne una nuova.
          </div>
        </div>
      ) : loading ? (
        <div className="text-slate-500 text-center py-10">Caricamento slot disponibili…</div>
      ) : (
        <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 md:p-7 space-y-6" data-testid="prenotazione-form">
          <div>
            <h2 className="text-lg font-extrabold text-[#0a1f55]">Seleziona un orario</h2>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Slot da 30 minuti nei prossimi 21 giorni. La segreteria confermerà via email.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto pr-2">
            {Object.entries(slotsByDay).map(([day, daySlots]) => (
              <div key={day} className="rounded-xl border border-slate-200 p-3">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-[#0a1f55] mb-2">
                  {fmtDay(daySlots[0].slot)}
                </div>
                <div className="space-y-1.5">
                  {daySlots.map((s) => {
                    const isSelected = selected === s.slot;
                    const disabled = s.occupato;
                    return (
                      <button
                        key={s.slot}
                        type="button"
                        disabled={disabled}
                        onClick={() => setSelected(s.slot)}
                        data-testid={`slot-${s.slot}`}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                          disabled
                            ? "bg-slate-100 text-slate-400 line-through cursor-not-allowed"
                            : isSelected
                            ? "bg-[#f59e0b] text-[#0a1f55] shadow-md"
                            : "bg-slate-50 hover:bg-[#f59e0b]/10 text-slate-700"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3" /> {fmtHour(s.slot)}
                        </span>
                        {isSelected && <CheckCircle2 className="h-4 w-4" />}
                        {disabled && <XCircle className="h-3 w-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {selected && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="slot-selected-preview">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-[13.5px] text-emerald-900">
                <strong>Slot selezionato:</strong> {fmtFull(selected)}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11.5px] font-bold text-[#0a1f55] uppercase tracking-widest">Argomento *</Label>
              <Input
                value={argomento}
                onChange={(e) => setArgomento(e.target.value)}
                required
                data-testid="input-argomento"
                placeholder="Es. Truffa online, contratto SPID, privacy..."
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-[11.5px] font-bold text-[#0a1f55] uppercase tracking-widest">Telefono per il richiamo</Label>
              <Input
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                data-testid="input-telefono"
                placeholder="+39 333 1234567"
                className="mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11.5px] font-bold text-[#0a1f55] uppercase tracking-widest">Descrizione (opzionale)</Label>
            <Textarea
              rows={4}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              data-testid="input-descrizione"
              placeholder="Descrivi brevemente il tuo caso per aiutare l'avvocato a prepararsi..."
              className="mt-1.5"
              maxLength={2000}
            />
            <div className="mt-1 text-[11px] text-slate-400 text-right">{descrizione.length}/2000</div>
          </div>

          <div className="flex items-start gap-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <AlertTriangle className="h-4 w-4 text-slate-500 mt-0.5" />
            <div className="text-[12px] text-slate-600">
              L&apos;appuntamento sarà confermato dalla segreteria via email entro 48 ore lavorative.
              Se lo slot risultasse non disponibile, ti proporremo un&apos;alternativa.
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || !selected || !argomento}
            data-testid="submit-prenotazione"
            className="w-full h-12 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold text-base"
          >
            {submitting ? "Invio…" : "Invia richiesta di prenotazione"}
          </Button>
        </form>
      )}
    </div>
  );
}
